import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import Select from 'components/Common/Form/Input/Select'
import TextArea from 'components/Common/Form/Input/TextArea'
import { getData as getCountries } from 'country-list'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { useAppDispatch } from 'hooks/reduxHooks'
import { sendWaitListTourSoldOut } from 'actions/TourV2Actions'
import TextLink from 'components/Luxkit/TextLink'
import config from 'constants/config'
import CheckboxInput from 'components/Luxkit/Checkbox/CheckboxInput'

const {
  termsAndConditions,
  privacyPolicy,
} = config.contentLinks

const FormFieldsContainer = styled.div`
  margin-top: ${rem(16)};
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-column-gap: ${rem(12)};
    grid-template-columns: 1fr 1fr;

    .full-width-column {
      grid-column: span 2;
    }
  }
`

const OccupancyContainer = styled.div`
  margin-top: ${rem(4)};
`

const CommentField = styled(TextArea)`
  margin-top: ${rem(20)};
`

const TermsAndConditionsField = styled(CheckboxInput)`
  margin-top: ${rem(20)};
`

const ROOM_CAPACITIES: Array<App.RoomCapacity> = [{
  adults: 20,
  children: 10,
  infants: 0,
}]

interface MappedStateProps {
  firstName?: string;
  lastName?: string;
  phone?: string;
  phonePrefix?: string;
  email?: string;
  countryCode: string;
}
interface Props {
  offer: App.Tours.TourV2Offer;
  departure?: App.Tours.TourV2OfferDeparture;
}

function WaitingListRequestFormModal(props: Props & MappedStateProps) {
  const {
    firstName,
    lastName,
    phone,
    phonePrefix,
    countryCode,
    email,
    offer,
    departure,
  } = props

  const dispatch = useAppDispatch()

  const maxChildAge = useMemo(() => Object.values(offer.variations)[0].maxChildPriceAge, [offer.variations])
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>([{
    adults: 2,
    children: 0,
  }])
  const handleOccupancies = useCallback((occupancies: Array<App.Occupants>) => setOccupancies(occupancies), [setOccupancies])
  const tourUrl = useMemo(() => window.location.href, [])

  const modalState = useModalElementContext<unknown>()

  const sendRequest = useCallback<FormEventHandler<HTMLFormElement>>((e) => {
    e.preventDefault()
    const requestWaitingListTour: App.Tours.WaitingListTourRequest = formToObject(e.currentTarget)
    const fullPayload: App.Tours.WaitingListTourRequest = {
      ...requestWaitingListTour,
      occupancy: occupancies.map(occupant => ({
        adults: occupant.adults,
        children: occupant.children,
        childrenAge: occupant.childrenAge,
      })),
    }
    dispatch(sendWaitListTourSoldOut(fullPayload))
    modalState.resolve(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, occupancies])

  return <ModalBase size="S">
    <form onSubmit={sendRequest}>
      <ModalHeader title="Join the waitlist" />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">
            Please let us know your details and our Tours Concierge will let you know if a spot on this tour becomes available.
          </BodyText>
          <FormFieldsContainer>
            <HiddenInput name="tourId" value={offer.id} />
            <HiddenInput name="tourUrl" value={tourUrl} />
            {
            !!departure &&
              <>
                <HiddenInput name="departureStartDate" value={departure.startDate} />
                <HiddenInput name="departureEndDate" value={departure.endDate} />
              </>
            }
            <TextInput
              label="First name"
              name="firstName"
              required
              placeholder="First name"
              defaultValue={firstName}
            />
            <TextInput
              label="Last name"
              name="lastName"
              required
              placeholder="Last name"
              defaultValue={lastName}
            />
            <TextInput
              className="full-width-column"
              type="email"
              label="Email address"
              name="email"
              required
              defaultValue={email}
            />
            <PhoneInput
              label="Mobile number"
              name="phone"
              id="mobileNumber"
              required
              defaultPhoneNumber={phone}
              defaultPhonePrefix={phonePrefix}
              prefixCountry={countryCode}
            />
            <Select
              label="Your home country"
              name="countryCode"
              defaultValue={countryCode}
              required
              noValidationSpacing
            >
              {getCountries().map(country =>
                <option key={country.code} value={country.code}>{country.name}</option>,
              )}
            </Select>
            <BodyText className="full-width-column" weight="bold" variant="medium">Who are you travelling with?</BodyText>
            <OccupancyContainer className="full-width-column">
              <OccupancyPicker
                compact
                name="occupancy"
                maxRooms={10}
                capacities={ROOM_CAPACITIES}
                occupancies={occupancies}
                childrenAgeLabel="at time of departure"
                maxChildAge={maxChildAge}
                onChange={handleOccupancies}
                enableMultiRoom={false}
                disableSaleUnit
              />
            </OccupancyContainer>
            <CommentField
              className="full-width-column"
              name="comment"
              noValidationSpacing
              resize="vertical"
              label="Any other questions or comments?"
              placeholder="Leave a message"
              maxLength={150}
            />
            <TermsAndConditionsField className="full-width-column" required size="large">
              By continuing, you agree to our <TextLink to={termsAndConditions.to} href={termsAndConditions.href} target="_blank" weight="regular">
                Terms &amp; Conditions
              </TextLink> and <TextLink to={privacyPolicy.to} href={privacyPolicy.href} target="_blank" weight="regular">
                Privacy Policy
              </TextLink>, and to subscribe to marketing communications for offers, alerts and services.
            </TermsAndConditionsField>
          </FormFieldsContainer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Submit request',
          type: 'submit',
        }}
      />
    </form>
  </ModalBase>
}

const mapStateToProps = (state: App.State): MappedStateProps => {
  return {
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    phone: state.auth.account.phone,
    phonePrefix: state.auth.account.phonePrefix,
    email: state.auth.account.email,
    countryCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(WaitingListRequestFormModal)
